import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import {
  allAlarmCodes as fallbackAlarmCodes,
  createStationCodesMap,
  deviceGroups,
} from './constants'
import type { AlarmCodeDef, DeviceGroupKey, StationRow } from './types'
import * as api from '@/api/alarm'
import { eui } from 'ems-ui'

const AlarmTypeMap = {
  '01': 'PCS',
  '02': 'BCS',
  '03': 'BMS',
  '04': 'eLink',
  '05': 'Water',
  '06': 'Fire',
  '07': '100C-DCDC',
  '08': '100C-MPPT',
  'E1': 'eBlock',
  'E2': 'EMS',
}

// ---- 服务器告警码 → AlarmCodeDef 映射 ----

/** 服务器返回的原始告警码条目 */
interface ServerAlarmCodeItem {
  id: number
  productModel: string
  type: keyof typeof AlarmTypeMap
  moduleType: string
  code: string
  alarmCode: string
  level: number
  description: string
  details: string
  descriptionEn: string
  detailsEn: string
  descriptionIta: string
  detailsTta: string
  /** 是否给客户展示 */
  state: boolean,
  /** 是否给客户推送，后端未返回时按 false 处理 */
  pushToCustomer?: boolean,
  group?: DeviceGroupKey
}

/**
 * 根据告警码的描述文本将其归类到设备分组。
 * 匹配顺序：HVAC → 消防 → PCS → BMS → EMS（默认）
 */
function mapServerAlarmToGroup(item: ServerAlarmCodeItem): DeviceGroupKey {
  return AlarmTypeMap[item.type] as DeviceGroupKey
}

/** 将服务器返回的单条告警码转换为 AlarmCodeDef */
function mapServerItemToAlarmCode(item: ServerAlarmCodeItem): AlarmCodeDef {
  return {
    code: item.alarmCode,
    name: item.description,
    group: mapServerAlarmToGroup(item),
    desc: item.details,
  }
}

/**
 * 从 axios 响应中提取告警码数组。
 * 兼容 `{ code:'SUCCESS', data:[…] }`、`{ code:200, rows:[…] }`、纯数组 等常见后端包装。
 */
function extractAlarmCodeList(res: any): ServerAlarmCodeItem[] | null {
  if (Array.isArray(res)) return res
  const list = res?.data ?? res?.rows ?? res?.list ?? res?.records
  return Array.isArray(list) ? list : null
}

// ---- composable ----

/**
 * 电站告警码（设备）：多选电站，批量配置其告警码开关。
 * curCodes 为批量视图——选中电站间一致时为该值，混合时为 false 且 mixedCodes 标记。
 * @param stations 全部电站列表，由上层注入以保证单例共享。
 */
export function useStationAlarmCodes(stations: ComputedRef<StationRow[]>) {
  const codeStationSearch = ref('')
  const selectedCodeStationIds = ref<string[]>([])
  const curCodes = reactive<Record<string, boolean>>({}) // 保存告警主键ID与告警是否生效配置
  const curPushCodes = reactive<Record<string, boolean>>({}) // 告警主键ID -> 是否推送给客户
  const mixedCodes = reactive<Record<string, boolean>>({}) // true 表示选中电站间该码状态不一致
  const codeLoading = ref(false) // 拉取告警码列表时 loading
  const savingCodeIds = reactive(new Set<string>()) // 正在保存中的告警码 ID（展示开关）
  const savingPushIds = reactive(new Set<string>()) // 正在保存中的告警码 ID（推送开关）

  // 告警码定义列表（响应式）：先用静态默认值，服务器数据到达后替换
  const allAlarmCodes = ref<AlarmCodeDef[]>([...fallbackAlarmCodes])
  const serverAlarmCodes = ref<ServerAlarmCodeItem[]>([])

  // 每电站的告警码开关状态；未配置的电站默认全部启用
  const stationCodesMap: Record<string, Record<string, boolean>> = createStationCodesMap()

  const filteredCodeStations = computed(() =>
    codeStationSearch.value
      ? stations.value.filter((s) => s.name.includes(codeStationSearch.value))
      : stations.value,
  )

  // 选中的电站对象列表（保持 stations 原顺序）
  const selectedCodeStations = computed(() =>
    stations.value.filter((s) => selectedCodeStationIds.value.includes(s.id.toString())),
  )

  // 某电站某告警码的有效状态（无记录默认开启）
  function stationCodeEnabled(sid: string, code: string) {
    const m = stationCodesMap[sid]
    return m && code in m ? !!m[code] : true
  }

  // 依据当前设备分组过滤告警码列表（响应式）
  function codeGroupAlarms(k: DeviceGroupKey) {
    return serverAlarmCodes.value.filter((c) => c.group === k)
  }

  // 依据当前选中电站集合，重算批量视图 curCodes / mixedCodes
  function recomputeCurCodes() {
    // const ids = selectedCodeStationIds.value
    serverAlarmCodes.value.forEach((c) => {
      curCodes[c.id] = c.state
      // 后端未返回该字段时默认不推送
      curPushCodes[c.id] = c.pushToCustomer === true
    })
  }

  // 选中某电站（单选）：切换时自动从服务器拉取该站告警码
  function toggleCodeStation(s: StationRow) {
    selectedCodeStationIds.value = [s.id]
    fetchAlarmCodesFromServer()
    recomputeCurCodes()
  }

  function isCodeStationSelected(s: StationRow) {
    return selectedCodeStationIds.value.includes(s.id)
  }

  // 当前过滤结果是否已全部选中
  const isAllCodeStationsSelected = computed(() => {
    const list = filteredCodeStations.value
    return list.length > 0 && list.every((s) => selectedCodeStationIds.value.includes(s.id.toString()))
  })

  // 左侧全选/清空（基于当前过滤结果）
  function toggleAllCodeStations() {
    if (isAllCodeStationsSelected.value) {
      const filteredIds = new Set(filteredCodeStations.value.map((s) => s.id))
      selectedCodeStationIds.value = selectedCodeStationIds.value.filter((id) => !filteredIds.has(id))
    } else {
      const set = new Set(selectedCodeStationIds.value)
      filteredCodeStations.value.forEach((s) => set.add(s.id.toString()))
      selectedCodeStationIds.value = Array.from(set)
    }
    recomputeCurCodes()
  }

  function codeEnabledCount(s: StationRow) {
    return allAlarmCodes.value.filter((c) => stationCodeEnabled(s.id, c.code)).length
  }

  function codeGroupEnabledCount(k: DeviceGroupKey) {
    return codeGroupAlarms(k).filter((c) => curCodes[c.code]).length
  }

  // 用户拨动某行开关：清除该行混合标记（保存时即会统一写回选中电站）
  function onToggleCode(id: string) {
    mixedCodes[id] = false
  }

  /** 单个告警码「客户展示」开关切换后立即调用接口保存 */
  async function onToggleCodeAndSave(code: ServerAlarmCodeItem, newValue: boolean|null) {
    if (newValue == null) {
      return
    }
    const id = String(code.id)
    curCodes[id] = newValue
    mixedCodes[id] = false
    savingCodeIds.add(id)
    try {
      await api.changeStationAlarmCode({
        // 两个开关同属一条记录，必须整条提交，否则未提交的那个会被覆盖
        alarmCodeList: [{
          id,
          alarmCode: code.alarmCode,
          state: newValue,
          pushToCustomer: curPushCodes[id] === true,
        }],
        stationIdList: [...selectedCodeStationIds.value],
      })
      // 与服务器一致后同步本地原始数据，避免重算时被旧值覆盖
      code.state = newValue
      eui.showToast('设置成功', 'success')
    } catch {
      curCodes[id] = !newValue
      eui.showToast('设置失败', 'error')
    } finally {
      savingCodeIds.delete(id)
    }
  }

  /** 单个告警码「客户推送」开关切换后立即调用接口保存 */
  async function onTogglePushAndSave(code: ServerAlarmCodeItem, newValue: boolean|null) {
    if (newValue == null) {
      return
    }
    const id = String(code.id)
    const prev = curPushCodes[id] === true
    curPushCodes[id] = newValue
    savingPushIds.add(id)
    try {
      await api.changeStationAlarmCode({
        alarmCodeList: [{
          id,
          alarmCode: code.alarmCode,
          state: curCodes[id] === true,
          pushToCustomer: newValue,
        }],
        stationIdList: [...selectedCodeStationIds.value],
      })
      code.pushToCustomer = newValue
      eui.showToast('设置成功', 'success')
    } catch {
      curPushCodes[id] = prev
      eui.showToast('设置失败', 'error')
    } finally {
      savingPushIds.delete(id)
    }
  }

  /** 全开/全关：同时作用于「客户展示」和「客户推送」两个开关 */
  function toggleAllCodes(v: boolean) {
    eui.confirm('系统提示', v?'确定要全部开启吗?':'确定要全部关闭吗?').then(ok => {
      if (ok) {
        serverAlarmCodes.value.forEach((c) => {
          curCodes[c.id] = v
          curPushCodes[c.id] = v
        })

        applyCurCodesToSelected()
      }
    })
  }

  // 组装接口需要的数据
  function applyCurCodesToSelected() {

    const alarmConfig = {
      alarmCodeList: Object.keys(curCodes).map(id => {
        return {
          id: id,
          alarmCode: '',
          state: curCodes[id],
          pushToCustomer: curPushCodes[id] === true
        }
      }),
      stationIdList: selectedCodeStationIds.value
    }

    api.changeStationAlarmCode(alarmConfig).then(res => {
      // 与服务器一致后同步本地原始数据，避免 recomputeCurCodes 用旧值覆盖界面
      serverAlarmCodes.value.forEach((c) => {
        if (curCodes[c.id] !== undefined) c.state = curCodes[c.id]
        if (curPushCodes[c.id] !== undefined) c.pushToCustomer = curPushCodes[c.id]
      })
      eui.showToast('保存成功', 'success')
    }).catch(e => {
      eui.showToast('保存失败', 'error')
    })

  }

  /** 从服务器拉取当前选中电站的告警码定义及开关状态 */
  function fetchAlarmCodesFromServer() {
    const sid = selectedCodeStationIds.value[0]
    if (!sid) return
    codeLoading.value = true
    api.getStationAlarmCodeList(sid).then((res: any) => {
      const list = extractAlarmCodeList(res)
      if (!list || !list.length) {
        console.warn('[AlarmConfig] 服务器返回空告警码列表，保持静态默认值')
        return
      }
      // mapServerAlarmToGroup
      serverAlarmCodes.value = list.map(item => {
        return {
          ...item,
          group: mapServerAlarmToGroup(item),
        }
      })

      // 替换告警码定义
      allAlarmCodes.value = list.map(mapServerItemToAlarmCode)


      // 同步该电站的开关状态：服务器 state 字段为 false 表示未启用
      const codeMap: Record<string, boolean> = {}
      list.forEach((item) => {
        codeMap[item.alarmCode] = item.state !== false
      })
      stationCodesMap[sid] = codeMap
      // 刷新当前视图
      recomputeCurCodes()
    }).catch((err: any) => {
      console.warn('[AlarmConfig] 拉取告警码失败，继续使用静态默认值:', err)
    }).finally(() => {
      codeLoading.value = false
    })
  }

  function saveStationCodeConfig() {
    if (!selectedCodeStationIds.value.length) {
      ElMessage.warning('请先选择电站')
      return
    }
    applyCurCodesToSelected()
    ElMessage.success(`已保存 ${selectedCodeStationIds.value.length} 个电站的告警码配置`)
  }

  return {
    deviceGroups,
    allAlarmCodes,
    codeGroupAlarms,
    codeStationSearch,
    selectedCodeStationIds,
    selectedCodeStations,
    curCodes,
    curPushCodes,
    mixedCodes,
    stationCodesMap,
    filteredCodeStations,
    stationCodeEnabled,
    recomputeCurCodes,
    toggleCodeStation,
    isCodeStationSelected,
    isAllCodeStationsSelected,
    toggleAllCodeStations,
    codeEnabledCount,
    codeGroupEnabledCount,
    onToggleCode,
    onToggleCodeAndSave,
    onTogglePushAndSave,
    codeLoading,
    savingCodeIds,
    savingPushIds,
    toggleAllCodes,
    applyCurCodesToSelected,
    saveStationCodeConfig,
    fetchAlarmCodesFromServer,
  }
}
